"use strict";

define('rs/services/workflow.service',['./abstract.service', 'rs/models/task', 'rs/models/task.summary', 'rs/models/workflow'], function (AbstractService, Task, TaskSummary, Workflow) {
  var WorkflowService = AbstractService.extend({
    properties: ['documentService'],
    getDocumentId: function getDocumentId() {
      return this.getDocumentService().getDocumentId();
    },
    getDocument: function getDocument() {
      return this.getDocumentService().getDocument();
    },
    configure: function configure() {
      this._super.apply(this, arguments);
      if (!this.getDocumentService()) {
        throw new Error('You cannot use WorkflowService without setting it up with DocumentService');
      }
      return this;
    },
    start: function start() {
      return Workflow.requestStart(this.getDocumentId());
    },
    stop: function stop() {
      return Workflow.requestCancel(this.getDocumentId());
    },
    restart: function restart() {
      return this.stop().bind(this).then(this.start);
    },
    hasTask: function hasTask(taskTypeName) {
      return _.find(this.getTasks(), function (task) {
        return task.getTaskTypeTaskName() == taskTypeName;
      });
    },
    getTasks: function getTasks(force) {
      return this.cache('tasks', this.requestTasks, force);
    },
    getTaskSummaries: function getTaskSummaries(force) {
      return this.cache('taskSummaries', this.requestTaskSummaries, force);
    },
    getTaskHistory: function getTaskHistory(force) {
      return this.cache('taskHistory', this.requestTaskHistory, force);
    },
    getTask: function getTask() {
      return (this.getTasks() || [])[0];
    },
    getTaskDisplayName: function getTaskDisplayName() {
      var task = this.getTask();
      return task ? task.getTaskDisplayName() : '';
    },
    getTaskName: function getTaskName() {
      var task = this.getTask();
      return task ? task.getTaskTypeTaskName() : '';
    },
    getTaskAnswers: function getTaskAnswers() {
      if (!this.getTask()) {
        return [];
      }
      var task = this.getTask(),
        taskAnswerList = task.getTaskTypeTaskAnswers() || [],
        actionMap = this.getStepDefinition(task.getTaskTypeTaskName()).answers || {},
        len = taskAnswerList.length,
        answers = [],
        answer = null,
        def = null,
        i = 0;
      taskAnswerList = this.filterTaskAnswers(taskAnswerList, task);
      for (i = 0; i < len; i++) {
        answer = taskAnswerList[i];
        def = actionMap[answer];
        if (def) {
          answers.push({
            displayName: this.getTaskAnswerDisplayName(def, task.getTaskTypeTaskName(), answer) || answer,
            confirm: def.confirm,
            icon: def.icon || 'chevron-right',
            sort: def.sort || 0,
            rightAlign: !!def.rightAlign,
            value: answer
          });
        }
      }
      answers = _.sortBy(answers, function (a) {
        return a.sort;
      });
      return answers;
    },
    getAnswerDefinitionByValue: function getAnswerDefinitionByValue(value) {
      value = String(value).toUpperCase();
      return _.find(this.getTaskAnswers(), function (answer) {
        return String(answer.value).toUpperCase() == value;
      });
    },
    getAnswerConfirmationByValue: function getAnswerConfirmationByValue(value) {
      var answer = this.getAnswerDefinitionByValue(value);
      if (answer && answer.confirm) {
        return answer.confirm;
      }
      return "Are you sure you want to continue?";
    },
    getTaskAnswerDisplayName: function getTaskAnswerDisplayName(answerDef, taskTypeName, answer) {
      var decision = this.getDocumentService().getOpenDecision(),
        decisionDescription = decision ? decision.getDecisionTypeDescription() : null;
      switch (taskTypeName) {
        case 'IRB_EXEMPT_REVIEW_COMPLETE':
        case 'EXPEDITED_REVIEW_COMPLETE':
        case 'ADMINISTRATIVE_REVIEW_COMPLETE':
        case 'LIMITED_IRB_REVIEW_COMPLETE':
          if (answer == 'YES') {
            switch (decisionDescription) {
              case 'Not Exempt':
              case 'Not Expedited':
              case 'Not Limited IRB':
                return 'Re-Assign';
            }
          }
          break;
      }
      return answerDef.name;
    },
    getStepDefinition: function getStepDefinition(taskName) {
      return this.constructor.WORKFLOW_STEPS[taskName] || {};
    },
    canMakeChildDecision: function canMakeChildDecision() {
      var task = this.getTask();
      if (task) {
        var taskName = task.getTaskTypeTaskName();
        switch (taskName) {
          case 'IRB_MEMBER_EXPEDITED_REVIEW':
          case 'LIMITED_IRB_MEMBER_REVIEW':
            return true;
        }
      }
      return false;
    },
    getTaskOperations: function getTaskOperations() {
      if (!this.getTask()) {
        return [];
      }
      var task = this.getTask(),
        activities = this.getAvailableActivities(task),
        finalActivities = [],
        activity = null,
        i = 0;
      for (i = 0; activity = _.clone(activities[i]); ++i) {
        activity.complete = this.isActivityComplete(activity.action);
        if (activity.complete) {
          activity.icon = 'check';
        }
        finalActivities.push(activity);
      }
      return finalActivities;
    },
    isActivityComplete: function isActivityComplete(action) {
      var documentService = this.getDocumentService(),
        assignments = this.getDocumentService().getAssignments();
      switch (action) {
        case 'assignPrincipalInvestigator':
          return assignments.hasAssignment('PRINCIPAL_INVESTIGATOR');
        case 'assignPrimaryContact':
          return assignments.hasAssignment('PRIMARY_CONTACT');
        case 'assignAnalyst':
          return assignments.hasAssignment('IRB_ANALYST');
        case 'setupBoard':
          return (assignments.hasAssignment('IRB_REVIEWER_PRIMARY') || assignments.hasAssignment('IRB_REVIEWER')) && assignments.hasAssignment('IRB_REVIEW_TYPE');
        case 'makeDecision':
          return documentService.getResponsibleDecision() && documentService.getResponsibleDecision().decisionType;
        case 'manageLetters':
          return !_.isEmpty(documentService.getOpenLetters());
        case 'assignToMeeting':
          return !_.isEmpty(documentService.getOpenMeetings());
      }
      return false;
    },
    getAvailableActivities: function getAvailableActivities(task) {
      var activities = this.getStepDefinition(task.getTaskTypeTaskName()).activities || [],
        available = activities.slice(0);
      if (task.getTaskTypeTaskName() == 'EDIT_COMPLETE' || task.getTaskTypeTaskName() == 'IRB_LEGACY_COMPLETE' || task.getTaskTypeTaskName() == 'IRB_ADMIN_EDIT') {
        var templateType = this.getDocument().getTemplateType();
        if (templateType != 'Initial' && templateType != 'Modification') {
          available = _.filter(available, function (activity) {
            var action = activity.action;
            return action != 'assignPrincipalInvestigator' && action != 'assignPrimaryContact';
          });
        }
      }
      return available;
    },
    filterTaskAnswers: function filterTaskAnswers(answerList) {
      var task = this.getTask(),
        filteredAnswers = [];
      switch (task.getTaskTypeName()) {
        case "IRB_FULL_REVIEW_COMPLETE":
          var openLetters = this.getDocumentService().getOpenLetters(),
            decision = this.getDocumentService().getOpenDecision();
          if (openLetters && openLetters.length || decision && decision.getDecisionType()) {
            filteredAnswers = _.filter(answerList, function (a) {
              return a == 'YES';
            });
          } else {
            filteredAnswers = answerList;
          }
          break;
        default:
          filteredAnswers = answerList;
          break;
      }
      return filteredAnswers;
    },
    answerTask: function answerTask(answer) {
      var task = this.getTask(),
        documentService = this.getDocumentService();
      if (!task) {
        throw new Error('You cannot set a Task Answer because you do not have any tasks');
      }
      return task.requestSetValue(answer).bind(this).tap(function () {
        return Promise.join(this.update('tasks'), this.update('taskSummaries'), documentService.update('document'));
      }).tap(function () {
        documentService.trigger('workflow:progress');
      });
    },
    requestTasks: function requestTasks() {
      return Promise.join(Task.requestTasksByDocument(this.getDocumentId()), this.getDocumentService().getDecisions(), this.getDocumentService().getLetters(), this.getDocumentService().getChecklists(), this.getDocumentService().getHistoricalChecklists()).bind(this).spread(function (tasks) {
        return _.sortBy(tasks, function (task) {
          return task.getTaskTypePassive();
        });
      });
    },
    requestTaskSummaries: function requestTaskSummaries() {
      return TaskSummary.requestAll(this.getDocumentId());
    },
    requestTaskHistory: function requestTaskHistory() {
      return TaskSummary.requestHistory(this.getDocumentId());
    },
    parseTaskSummaryResponse: function parseTaskSummaryResponse(taskMap) {
      var taskSummaryMap = {},
        taskNames = _.keys(taskMap);
      for (var i = 0, len = taskNames.length, taskName; i < len; i++) {
        taskName = taskNames[i];
        if (!_.isEmpty(taskMap[taskName])) {
          taskSummaryMap[taskName] = TaskSummary.models(taskMap[taskName]);
        }
      }
      return taskSummaryMap;
    }
  }, {
    requestTasks: function requestTasks(params) {
      var taskDef = null;
      return Task.requestTasks(params).bind(this).each(function (task) {
        var taskType = task.get('taskType');
        if ((taskDef = this.WORKFLOW_STEPS[taskType]) && taskDef.task) {
          task.set('taskAction', taskDef.task);
        }
      });
    },
    WORKFLOW_STEPS: {
      EDIT_COMPLETE: {
        answers: {
          YES: {
            name: 'Complete Submission'
          }
        },
        activities: [{
          displayName: 'Assign PI',
          action: 'assignPrincipalInvestigator',
          icon: 'user'
        }, {
          displayName: 'Assign PC',
          action: 'assignPrimaryContact',
          icon: 'user'
        }],
        task: 'Complete Submission'
      },
      IRB_ADMIN_EDIT: {
        answers: {
          YES: {
            name: 'Complete Submission'
          }
        }
      },
      CERTIFY: {
        answers: {
          YES: {
            name: 'Certify',
            confirm: 'I confirm that I have the proper training, expertise and resources to conduct this study. I understand and accept my responsibilities as the [ROLE] for this study. I confirm that I have no significant financial conflict of interest in this project or have disclosed a conflict per institutional policies and federal requirements. I confirm that the information provided in this application is true, complete, and accurate to the best of my knowledge; that any false, fictitious, or fraudulent statements or claims may subject me to criminal, civil, or administrative penalties; and agree to accept responsibility for the oversight and scientific conduct of the project.',
            sort: 1
          },
          NO: {
            name: 'Return To Investigators',
            sort: 0,
            rightAlign: true,
            icon: 'chevron-left'
          }
        },
        activities: [],
        task: 'Certify Submission'
      },
      ORG_APPROVED: {
        answers: {
          YES: {
            name: 'Approve',
            confirm: 'I understand and agree with the scientific aims of the protocol and feel that the information provided in this application is complete and accurate. I assure that the PI has proper training, expertise and resources to conduct the study and understands his/her responsibilities to comply with all applicable institutional, local, state and federal regulations. I am available to provide the necessary oversight and guidance to the PI. I will report any known regulatory noncompliance as per institutional policies and regulatory requirements.',
            sort: 1
          },
          NO: {
            name: 'Reject',
            sort: 0,
            rightAlign: true,
            icon: 'chevron-left'
          }
        },
        activities: [],
        task: 'Organization Approval Needed'
      },
      IRB_ANALYST_SELECTED: {
        answers: {
          YES: {
            name: 'Proceed'
          }
        },
        activities: [{
          displayName: 'Assign Analyst',
          action: 'assignAnalyst',
          select: true,
          icon: 'user'
        }],
        task: 'Assign Analyst'
      },
      IRB_ANALYST_REVIEW_CLOSE: {
        answers: {
          YES: {
            name: 'Withdraw Submission',
            sort: 1
          },
          NO: {
            name: 'Return To Investigators',
            sort: 0,
            rightAlign: true,
            icon: 'chevron-left'
          }
        }
      },
      IRB_ANALYST_TASKS_DONE: {
        answers: {
          YES: {
            name: 'Proceed to Reviewers',
            sort: 2
          },
          NO: {
            name: 'Return To Investigators',
            sort: 0,
            rightAlign: true,
            icon: 'chevron-left'
          },
          WITHDRAWL: {
            name: 'Withdrawal',
            sort: 1
          }
        },
        activities: [{
          displayName: 'Assign Reviewers',
          action: 'setupBoard'
        }],
        task: 'Complete Analyst Pre-Review'
      },
      IRB_FULL_REVIEW_COMPLETE: {
        answers: {
          YES: {
            name: 'Review Complete',
            sort: 1
          },
          NO: {
            name: 'Return To Investigators',
            sort: 0,
            rightAlign: true,
            icon: 'chevron-left'
          }
        },
        activities: [{
          displayName: 'Assign to Meeting',
          icon: 'users',
          action: 'assignToMeeting'
        }, {
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }, {
          displayName: 'Manage Letters',
          icon: 'envelope',
          action: 'manageLetters'
        }],
        task: 'Complete Post Review'
      },
      IRB_FULL_EXPEDITED_REVIEW: {
        answers: {
          YES: {
            name: 'Review Complete'
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }],
        task: 'Complete Expedited Review'
      },
      IRB_FULL_EXPEDITED_POST_REVIEW: {
        answers: {
          YES: {
            name: 'Post Review Complete'
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }, {
          displayName: 'Manage Letters',
          icon: 'envelope',
          action: 'manageLetters'
        }],
        task: 'Complete Post Review'
      },
      LIMITED_IRB_REVIEW_COMPLETE: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          REDO: {
            name: 'Redo Review',
            confirm: "Are you sure you want to redo the review?  This will remove any unsent letters that have been created."
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }, {
          displayName: 'Manage Letters',
          icon: 'envelope',
          action: 'manageLetters'
        }],
        task: 'Complete Post Review'
      },
      EXPEDITED_REVIEW_COMPLETE: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          REDO: {
            name: 'Redo Review',
            confirm: "Are you sure you want to redo the review?  This will remove any unsent letters that have been created."
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }, {
          displayName: 'Manage Letters',
          icon: 'envelope',
          action: 'manageLetters'
        }],
        task: 'Complete Post Review'
      },
      ADMINISTRATIVE_REVIEW_COMPLETE: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          REDO: {
            name: 'Redo Review',
            confirm: "Are you sure you want to redo the review?  This will remove any unsent letters that have been created."
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }, {
          displayName: 'Manage Letters',
          icon: 'envelope',
          action: 'manageLetters'
        }],
        task: 'Complete Post Review'
      },
      IRB_EXEMPT_POST_REVIEW: {
        answers: {
          YES: {
            name: 'Post Review Complete'
          },
          REDO: {
            name: 'Redo Review',
            confirm: "Are you sure you want to redo the review?  This will remove any unsent letters that have been created."
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }, {
          displayName: 'Manage Letters',
          icon: 'envelope',
          action: 'manageLetters'
        }],
        task: 'Complete Post Review'
      },
      IRB_EXEMPT_REVIEW_COMPLETE: {
        answers: {
          YES: {
            name: 'Proceed'
          },
          SWITCH: {
            name: 'Switch to Expedited'
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }],
        task: 'Complete Exempt Review'
      },
      LIMITED_IRB_PRIMARY_REVIEW: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          SWITCH: {
            name: 'Switch to Exempt'
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }],
        task: 'Complete Limited IRB Review'
      },
      LIMITED_IRB_MEMBER_REVIEW: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          NO: null
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }],
        task: 'Complete Limited IRB Review'
      },
      IRB_EXPEDITED_PRIMARY_REVIEW: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          SWITCH: {
            name: 'Switch to Exempt'
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }],
        task: 'Complete Expedited Review'
      },
      IRB_MEMBER_EXPEDITED_REVIEW: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          NO: null
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }],
        task: 'Complete Expedited Review'
      },
      IRB_MEMBER_ADMINISTRATIVE_REVIEW: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          SWITCH: {
            name: 'Switch to Exempt'
          }
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }],
        task: 'Complete Admin Level Review'
      },
      ADMINISTRATIVE_PRIMARY_REVIEW: {
        answers: {
          YES: {
            name: 'Review Complete'
          },
          NO: null
        },
        activities: [{
          displayName: 'Make Decision',
          icon: 'gavel',
          action: 'makeDecision'
        }],
        task: 'Complete Admin Level Review'
      },
      IRB_ANALYST_CLOSURE: {
        answers: {
          YES: {
            name: 'Close this study'
          },
          NO: {
            name: 'Return To Investigators',
            sort: 0,
            rightAlign: true,
            icon: 'chevron-left'
          }
        },
        activities: [{
          displayName: 'Manage Letters',
          icon: 'envelope',
          action: 'manageLetters'
        }],
        task: 'Complete Submission Closure'
      },
      IRB_ANALYST_WITHDRAWAL: {
        answers: {
          YES: {
            name: 'Withdraw this study',
            confirm: 'Are you sure you want to withdraw this submission?'
          },
          NO: {
            name: 'Return To Investigators',
            sort: 0,
            rightAlign: true,
            icon: 'chevron-left'
          }
        },
        activities: [{
          displayName: 'Manage Letters',
          icon: 'envelope',
          action: 'manageLetters'
        }],
        task: 'Complete Submission Withdrawal'
      },
      IRB_LEGACY_COMPLETE: {
        answers: {
          YES: {
            name: 'Complete Submission',
            confirm: 'Are you sure you want to complete this submission? No other changes or uploads will be able to be added.'
          }
        },
        activities: [],
        task: 'Complete Legacy Submission'
      },
      IRB_RESEARCHER_REVIEW: {
        task: 'View Submission'
      },
      IRB_MEMBER_REVIEW: {
        task: 'Review & Comment Submission'
      }
    }
  });
  return WorkflowService;
});

